import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { zodResolver } from '@hookform/resolvers/zod';
import { TextField, dobZodField } from '@vvoyages/shared-ui/ui';
import { z } from 'zod';

import Button from '@/components/Button';
import { DateOfBirthField } from '@/components/DateOfBirthField';
import { WizardStep } from '@/components/Wizard';
import { appUiIds } from '@/constants/appUiIds';
import { DOB_VALIDATION, getDobValidationMessages } from '@/constants/dobValidation';
import useAppUiData from '@/hooks/useAppUiData';
import { selectResources } from '@/reducers/common/selectors';

import styles from './ClaimBookingStep.module.scss';

const createClaimBookingSchema = (resources: Record<string, unknown>) =>
  z.object({
    name: z.string().min(1, 'Surname is required'),
    dob: dobZodField({
      ...getDobValidationMessages(resources, 'createAccount'),
      minAge: DOB_VALIDATION.minAge.createAccount,
      maxAge: DOB_VALIDATION.maxAge,
    }),
    bookingNumber: z
      .string()
      .min(1, 'Booking reference is required')
      .regex(/^[0-9]+$/, 'Please enter a valid booking reference')
      .min(6, 'Booking reference must be at least 6 digits')
      .max(10, 'Booking reference must be no more than 10 digits'),
  });

type ClaimBookingFormValues = z.infer<ReturnType<typeof createClaimBookingSchema>>;

interface ClaimBookingFormProps {
  tabId: string;
  // eslint-disable-next-line no-unused-vars
  onSubmit: Function;
  onCancel: () => void;
  isUseRecaptchaV2?: boolean;
  RecaptchaV2Component: React.ComponentType;
}

const ClaimBookingForm: React.FC<ClaimBookingFormProps> = ({
  tabId,
  onSubmit,
  onCancel,
  isUseRecaptchaV2,
  RecaptchaV2Component,
}) => {
  const appUiData = useAppUiData();
  const resources = useSelector(selectResources);
  const claimBookingSchema = createClaimBookingSchema(resources);

  const methods = useForm<ClaimBookingFormValues>({
    resolver: zodResolver(claimBookingSchema),
    mode: 'all',
    defaultValues: { name: '', dob: '', bookingNumber: '' },
  });

  const { control, handleSubmit } = methods;

  const onFormSubmit = handleSubmit(({ bookingNumber, name, dob }) => {
    onSubmit({
      bookingNumber,
      user: { lastName: name, birthday: dob },
      claimConfirmed: false,
      isManualEntry: true,
    });
  });

  return (
    <WizardStep tabId={tabId}>
      <h2 className={styles.title}>{appUiData[appUiIds.claimBooking.title]}</h2>
      <p className={styles.subtitle}>{appUiData[appUiIds.claimBooking.enterDetailsManualEntry]}</p>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <FormProvider {...methods}>
        <form onSubmit={onFormSubmit} className={styles.formContainer} noValidate>
          <div className={styles.claimForm}>
            <TextField name="name" control={control} label="Surname" required className={styles.claimBookingInput} />
            <DateOfBirthField
              useWheelPicker
              name="dob"
              control={control}
              labelVariant="fieldGroup"
              label="Date of Birth"
              required
              minAge={DOB_VALIDATION.minAge.createAccount}
              maxAge={DOB_VALIDATION.maxAge}
            />
            <div className={styles.inputWithToolTip}>
              <TextField
                name="bookingNumber"
                control={control}
                label="Booking Reference"
                required
                className={styles.claimBookingInput}
                tooltipContent={appUiData[appUiIds.claimBooking.bookingReference]}
              />
            </div>
            {isUseRecaptchaV2 && (
              <div className={styles.recaptchaContainer}>
                <RecaptchaV2Component />
              </div>
            )}
            <Button type="submit" variant="primary" className={styles.buttonClaimSubmit}>
              {appUiData[appUiIds.commonLabels.search]}
            </Button>
            <Button onClick={onCancel} className={styles.cancelLink}>
              {appUiData[appUiIds.commonLabels.cancel]}
            </Button>
          </div>
        </form>
      </FormProvider>
    </WizardStep>
  );
};

export default ClaimBookingForm;
